import { NestedTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { Tag } from 'functions/src/models/Tag';
import { PeriodService } from 'src/app/services/period.service';
import { PlannerComponent } from 'src/components/planner/planner.component';
import { TagNodeIconComponent } from 'src/components/tag-node-icon/tag-node-icon.component';
import { TagNodeSummaryComponent } from 'src/components/tag-node-summary/tag-node-summary.component';
import { TagNode } from 'src/services/tag-tree/TagNode';
import { DetailedTag } from 'src/services/tag/DetailedTag';
import { getLevelTag } from 'src/utils/getLevelTag';
import { getTopLevelTag } from 'src/utils/getTopLevelTag';
import { isVisible } from 'src/utils/isVisible';

@Component({
  selector: 'tag-tree',
  standalone: true,
  imports: [
    MatTreeModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    TagNodeSummaryComponent,
    TagNodeIconComponent,
    CommonModule,
  ],
  templateUrl: './tag-tree.component.html',
  styleUrl: './tag-tree.component.scss',
})
export class TagTreeComponent {
  @Input() tag: string = '';

  @Output() onTagClick = new EventEmitter<Tag>();

  treeControl = new NestedTreeControl<TagNode>((node) => node.children);
  dataSource = new MatTreeNestedDataSource<TagNode>();

  @Input()
  public set tagNodes(value: TagNode[]) {
    this._tagNodes = value;
    this.dataSource.data = value;
  }

  private _detailedTags: DetailedTag[] = [];

  @Input() set detailedTags(value: DetailedTag[]) {
    this._detailedTags = value;
    this.hardRefresh();
  }

  get detailedTags() {
    return this._detailedTags;
  }

  constructor(private periodService: PeriodService) {
    this.dataSource.data = [];
  }

  /**
   * Tree with nested child does not update the view so we need to hard refresh
   * This is only the case when the tag color is updated
   * So we force refresh the tree when Planner is submitted
   * https://github.com/angular/components/issues/11381
   */
  public hardRefresh() {
    const _data = this.dataSource.data;
    this.dataSource.data = [];
    this.dataSource.data = _data;
  }

  public get tagNodes() {
    return this._tagNodes;
  }

  private _tagNodes: TagNode[] = [];

  hasChildAndVisible = (_: number, node: TagNode) => {
    return this.hasChild(_, node) && this.isVisible(node);
  };

  hasChild = (_: number, node: TagNode) =>
    !!node.children && node.children.length > 0;

  isVisible(tag: TagNode) {
    return isVisible(tag, this.detailedTags, this.periodService.current);
  }

  trackByFn(_index: number, item: TagNode) {
    return item.fullPath;
  }

  getUrl(node: TagNode) {
    return node.fullPath ? node.fullPath.replace('#', '') : node.fullPath;
  }

  getIconFor(node: TagNode) {
    return 'balance';
  }

  getColorClass(tag: Tag) {
    return PlannerComponent.getColorClass(tag, this.detailedTags);
  }

  getTopLevelTag(tag: any) {
    return getTopLevelTag(tag, this.detailedTags);
  }

  getClassObject(node: TagNode): { [key: string]: boolean } {
    const pl = (getLevelTag(node) - 1) * 4;

    return {
      'bg-zinc-700': this.tag === node.fullPath,
      [this.getTopLevelTag(node)?.color]: true,
      [`pl-${pl}`]: true,
    };
  }

  getLevelTag(tag: Tag): number {
    return getLevelTag(tag);
  }
}
